import { Injectable } from '@angular/core';
import { PoiType, PoiTypeGroup } from './settings.models';

@Injectable({
  providedIn: 'root',
})
export class PoiTypeSettingsService {
  private hiddenPoiTypes = new Set<number>();

  /**
   * Load POI type groups from IVION SDK.
   * Groups POI types by their poiTypeGroup.
   * @param language - Language code for POI type names
   * @param outdoorDefectPoiTypeId - Optional ID for outdoor defect type (shows "Outdoor" if name is empty)
   */
  async loadPoiTypeGroups(language = 'en', outdoorDefectPoiTypeId?: number): Promise<PoiTypeGroup[]> {
    const ivApi = (window as any).IvionApp?.ivApi;
    if (!ivApi) {
      console.warn('[PoiTypeSettings] IVION API not available');
      return [];
    }

    try {
      const poiTypes = await ivApi.poi.poiTypeRepository.findAll();
      const groups: Map<number, PoiTypeGroup> = new Map();

      for (const pt of poiTypes) {
        const groupId = pt.poiTypeGroup.id;
        let groupName = pt.poiTypeGroup.name[language];
        if (!groupName) {
          groupName = pt.poiTypeGroup.name['en'] ?? 'Unknown';
        }

        let group = groups.get(groupId);
        if (!group) {
          group = {
            id: groupId,
            name: groupName,
            poiTypes: [],
          };
          groups.set(groupId, group);
        }

        let typeName = pt.name[language];
        if (!typeName) {
          typeName = pt.name['en'];
        }

        // Special handling for outdoor defect type (has empty name in IVION SDK)
        if (!typeName && pt.id === outdoorDefectPoiTypeId) {
          typeName = 'Outdoor';
        }

        if (!typeName) {
          typeName = 'Unknown';
        }

        group.poiTypes.push({
          id: pt.id,
          name: typeName,
          icon: pt.icon,
        });
      }

      return Array.from(groups.values());
    } catch (error) {
      console.error('[PoiTypeSettings] Failed to load POI types:', error);
      return [];
    }
  }

  /**
   * Show a POI type (make visible on map).
   */
  showPoiType(id: number): void {
    const ivApi = (window as any).IvionApp?.ivApi;
    if (!ivApi) {
      console.warn('[PoiTypeSettings] IVION API not available');
      return;
    }

    ivApi.legacyApi.setPoiTypesVisibility(true, [id]);
    this.hiddenPoiTypes.delete(id);
  }

  /**
   * Hide a POI type (remove from map).
   */
  hidePoiType(id: number): void {
    const ivApi = (window as any).IvionApp?.ivApi;
    if (!ivApi) {
      console.warn('[PoiTypeSettings] IVION API not available');
      return;
    }

    ivApi.legacyApi.setPoiTypesVisibility(false, [id]);
    this.hiddenPoiTypes.add(id);
  }

  /**
   * Toggle POI type visibility.
   */
  togglePoiType(id: number): void {
    if (this.isPoiTypeHidden(id)) {
      this.showPoiType(id);
    } else {
      this.hidePoiType(id);
    }
  }

  /**
   * Check if a POI type is currently hidden.
   */
  isPoiTypeHidden(id: number): boolean {
    return this.hiddenPoiTypes.has(id);
  }

  /**
   * Check if all specified POI types are hidden.
   */
  areAllHidden(ids: number[]): boolean {
    return ids.every((id) => this.hiddenPoiTypes.has(id));
  }

  /**
   * Get the set of hidden POI type IDs.
   */
  getHiddenPoiTypes(): Set<number> {
    return new Set(this.hiddenPoiTypes);
  }
}
